import { useState } from "react";
import { ArrowLeft, ToggleLeft, ToggleRight } from "lucide-react";
import { showError, showSuccess } from "../lib/toast";
import {
  server_post_data,
  vacancy_save_update,
} from "../serviceconnection/ServiceConnection";

// ─────────────────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  p_id: 0,
  designation: "",
  loaction: "Bhopal",
  no_of_position: "",
  qualification: "",
  skills: "",
  salary_details: "",
  min_experience: "",
  max_experience: "",
  job_description: "",
  image_name: "",
  status: 1,
};

// ── Reusable Field — defined OUTSIDE component to prevent remount ─────────────
const Field = ({
  label,
  name,
  placeholder,
  required,
  type = "text",
  textarea,
  form,
  handleChange,
}) => (
  <div className="space-y-1">
    <label className="text-xs font-semibold text-muted-foreground">
      {label}
      {required && <span className="text-destructive"> *</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={form[name]}
        onChange={handleChange}
        rows={6}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent resize-none"
      />
    ) : (
      <input
        name={name}
        value={form[name]}
        onChange={handleChange}
        type={type}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm text-foreground outline-none focus:border-accent"
      />
    )}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function VacancyForm({ editData, onBack, onSaved }) {
  const [form, setForm] = useState(() =>
    editData
      ? {
          p_id: editData.primary_id,
          designation: editData.designation ?? "",
          loaction: editData.loaction ?? "Bhopal",
          no_of_position: editData.no_of_position ?? "",
          qualification: editData.qualification ?? "",
          skills: editData.skills ?? "",
          salary_details: editData.salary_details ?? "",
          min_experience: editData.min_experience ?? "",
          max_experience: editData.max_experience ?? "",
          job_description: editData.job_description ?? "",
          image_name: editData.image_name ?? "",
          status: editData.status ?? 1,
        }
      : EMPTY_FORM,
  );
  const [loading, setLoading] = useState(false);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSave = async () => {
    if (
      !form.designation ||
      !form.qualification ||
      !form.skills ||
      !form.salary_details ||
      !form.job_description
    ) {
      showError("Please fill all required fields");
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
      const res = await server_post_data(vacancy_save_update, fd);
      if (res.data?.error) {
        showError(res.data?.message || "Save failed");
        return;
      }
      showSuccess(form.p_id > 0 ? "Vacancy updated!" : "Vacancy added!");
      onSaved();
    } catch {
      showError("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[800px] mx-auto space-y-5">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted border border-border text-muted-foreground"
            title="Back to list"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {form.p_id > 0 ? "Edit Vacancy" : "Add New Vacancy"}
            </h1>
            <p className="page-subtitle">
              {form.p_id > 0
                ? `Editing: ${form.designation || "Untitled"}`
                : "Fill in the details to post a new vacancy"}
            </p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-60"
          style={{ background: "hsl(var(--accent))" }}
        >
          {loading
            ? "Saving..."
            : form.p_id > 0
              ? "Update Vacancy"
              : "Add Vacancy"}
        </button>
      </div>

      {/* Form Card */}
      <div className="section-panel p-0">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Vacancy Details</h2>
        </div>
        <div className="section-panel-body space-y-5">
          {/* Row 1 — Designation + Location */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Designation"
              name="designation"
              placeholder="e.g. Python Developer"
              required
              form={form}
              handleChange={handleChange}
            />
            <Field
              label="Location"
              name="loaction"
              placeholder="e.g. Bhopal"
              form={form}
              handleChange={handleChange}
            />
          </div>

          {/* Row 2 — Positions + Salary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="No. of Positions"
              name="no_of_position"
              placeholder="e.g. 2"
              type="number"
              form={form}
              handleChange={handleChange}
            />
            <Field
              label="Salary Details"
              name="salary_details"
              placeholder="e.g. 3–5 LPA"
              required
              form={form}
              handleChange={handleChange}
            />
          </div>

          {/* Row 3 — Experience */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Min Experience (yrs)"
              name="min_experience"
              placeholder="e.g. 1"
              type="number"
              form={form}
              handleChange={handleChange}
            />
            <Field
              label="Max Experience (yrs)"
              name="max_experience"
              placeholder="e.g. 3"
              type="number"
              form={form}
              handleChange={handleChange}
            />
          </div>

          {/* Row 4 — Qualification */}
          <Field
            label="Qualification"
            name="qualification"
            placeholder="e.g. B.Tech / MCA / BCA"
            required
            form={form}
            handleChange={handleChange}
          />

          {/* Row 5 — Skills */}
          <Field
            label="Skills"
            name="skills"
            placeholder="e.g. Django, React, REST APIs"
            required
            form={form}
            handleChange={handleChange}
          />

          {/* Row 6 — Job Description */}
          <Field
            label="Job Description"
            name="job_description"
            placeholder="Describe the role, responsibilities..."
            required
            textarea
            form={form}
            handleChange={handleChange}
          />

          {/* Row 7 — Show on Website toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
            <div>
              <p className="text-sm font-semibold text-foreground">
                Show on Website
              </p>
              <p className="text-xs text-muted-foreground">
                Toggle visibility on the public careers page
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                setForm((p) => ({ ...p, status: p.status === 1 ? 0 : 1 }))
              }
              style={{
                color:
                  form.status === 1
                    ? "hsl(var(--success))"
                    : "hsl(var(--muted-foreground))",
              }}
            >
              {form.status === 1 ? (
                <ToggleRight className="w-8 h-8" />
              ) : (
                <ToggleLeft className="w-8 h-8" />
              )}
            </button>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-border">
            <button
              onClick={onBack}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-border text-foreground hover:bg-muted"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: "hsl(var(--accent))" }}
            >
              {loading
                ? "Saving..."
                : form.p_id > 0
                  ? "Update Vacancy"
                  : "Add Vacancy"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
